import React, { useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  ComboboxData,
  Group,
  NumberInput,
  Select,
  Table,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { IconLock, IconLockOpen, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGetPrescriptions } from '../../../global/entities/Prescription/queries/useGetPrescriptions.tsx';
import { useTable } from '../../../global/hooks/useTable.tsx';
import {
  IPrescription,
  PrescriptionLockedStatus,
} from '../../../global/entities/Prescription/Prescription.ts';
import { useDeletePrescription } from '../../../global/entities/Prescription/mutations/useDeletePrescriptionMutations.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';

interface INewPrescriptionTableRowProps {
  prescription: IPrescription;
  index: number;
  selected: boolean;
  onSelectChange: (id: number, selected: boolean) => void;
  onValidate: (id: number) => void;
  onUnlock: (id: number) => void;
}

const EditPrescriptionTableRow: React.FC<INewPrescriptionTableRowProps> =
  inject()(
    observer(
      ({
        prescription,
        index,
        selected,
        onSelectChange,
        onValidate,
        onUnlock,
      }) => {
        const { t, i18n } = useTranslation();
        const initialPrescription = prescription;
        const { mutate: deletePrescription } = useDeletePrescription();
        const { data: prescriptionData } = useGetPrescriptions();
        const { currentUser } = useSessionProvider();
        const isHCP = !!currentUser?.healthcareProfessionalID;

        const { setSortedData, errors } = useTable();

        const isLocked =
          prescription.lockedStatus === PrescriptionLockedStatus.Locked;

        const comboboxData: {
          doseFrequencies: ComboboxData;
          units: ComboboxData;
        } = useMemo(() => {
          return {
            doseFrequencies: prescriptionData?.doseFrequenciesCombobox ?? [],
            units: prescriptionData?.unitsCombobox ?? [],
          };
        }, [prescriptionData]);

        const updatePrescriptionRow = (value: any, id: number = index) => {
          setSortedData((prev) =>
            prev.map((drug) => {
              if (drug.id === id) return { ...drug, ...value };

              return drug;
            }),
          );
        };

        const onClickDelete = () => {
          if (isLocked) return;
          if (prescription?.id > 0)
            deletePrescription(initialPrescription.id.toString());

          setSortedData((prevRows) =>
            prevRows.filter((row) => row.id !== prescription?.id),
          );
        };

        const validatorTooltip = () => {
          if (!isLocked) return '';
          const ts = prescription.validatedAtUtc
            ? new Date(prescription.validatedAtUtc).toLocaleString(
                i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
                { dateStyle: 'medium', timeStyle: 'short' },
              )
            : '';
          return t('prescriptionHistory.badge.validatedBy', {
            name: prescription.validatedByName ?? '—',
            timestamp: ts,
          });
        };

        if (isLocked) {
          const unitLabel = comboboxData.units.find(
            (u: any) => Number(u.value) === prescription.unitID,
          ) as { label: string } | undefined;
          const freqLabel = comboboxData.doseFrequencies.find(
            (f: any) => Number(f.value) === prescription.doseFrequencyID,
          ) as { label: string } | undefined;
          return (
            <Table.Tr
              key={prescription.id}
              style={{ background: 'var(--mantine-color-gray-0)' }}
            >
              <Table.Td>
                <Tooltip
                  label={t('prescriptionHistory.edit.lockedRowSelectDisabled')}
                  withArrow
                >
                  <Checkbox
                    checked={selected}
                    onChange={(e) =>
                      onSelectChange(prescription.id, e.currentTarget.checked)
                    }
                    aria-label={t('prescriptionHistory.edit.selectRowAria')}
                  />
                </Tooltip>
              </Table.Td>
              <Table.Td w={250}>{prescription.drugName}</Table.Td>
              <Table.Td>{prescription.dose}</Table.Td>
              <Table.Td>{unitLabel?.label ?? prescription.unitID}</Table.Td>
              <Table.Td>
                {freqLabel?.label ?? prescription.doseFrequencyID}
              </Table.Td>
              <Table.Td>
                {prescription.startDate
                  ? new Date(prescription.startDate).toISOString().slice(0, 10)
                  : ''}
              </Table.Td>
              <Table.Td>
                {prescription.endDate
                  ? new Date(prescription.endDate).toISOString().slice(0, 10)
                  : t('prescriptionHistory.ongoing')}
              </Table.Td>
              <Table.Td>
                <Tooltip label={validatorTooltip()} withArrow multiline w={220}>
                  <Badge
                    color="green"
                    variant="light"
                    leftSection={<IconLock size={12} />}
                  >
                    {t('prescriptionHistory.badge.locked')}
                  </Badge>
                </Tooltip>
              </Table.Td>
              <Table.Td>
                {isHCP && (
                  <Button
                    size="xs"
                    variant="outline"
                    color="orange"
                    leftSection={<IconLockOpen size={12} />}
                    onClick={() => onUnlock(prescription.id)}
                  >
                    {t('prescriptionHistory.actions.unlock')}
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          );
        }

        return (
          <Table.Tr key={prescription.id}>
            <Table.Td>
              <Checkbox
                checked={selected}
                onChange={(e) =>
                  onSelectChange(prescription.id, e.currentTarget.checked)
                }
                aria-label={t('prescriptionHistory.edit.selectRowAria')}
                disabled={prescription.id < 0}
              />
            </Table.Td>
            <Table.Td w={250}>
              <TextInput
                value={prescription?.drugName}
                onChange={(ev) => {
                  updatePrescriptionRow({ drugName: ev.target.value });
                }}
                error={
                  errors.find(
                    (error) =>
                      error.id == prescription?.id && error.drugNameError,
                  )?.drugNameError
                }
              />
            </Table.Td>
            <Table.Td>
              <NumberInput
                value={prescription?.dose ?? ''}
                allowDecimal
                decimalScale={2}
                decimalSeparator="."
                allowedDecimalSeparators={[',']}
                step={0.5}
                hideControls
                onChange={(value) => {
                  if (value === '' || value === null || value === undefined) {
                    updatePrescriptionRow({ dose: undefined });
                    return;
                  }
                  const parsed =
                    typeof value === 'number' ? value : Number(value);
                  updatePrescriptionRow({
                    dose: Number.isFinite(parsed) ? parsed : undefined,
                  });
                }}
                error={
                  errors.find(
                    (error) => error.id === prescription?.id && error.doseError,
                  )?.doseError
                }
              />
            </Table.Td>
            <Table.Td>
              <Select
                defaultValue={prescription?.unitID?.toString() || '0'}
                data={comboboxData.units}
                onChange={(value) => {
                  updatePrescriptionRow({ unitID: Number(value) });
                }}
                error={
                  errors.find(
                    (error) =>
                      error.id == prescription?.id && error.unitIDError,
                  )?.unitIDError
                }
                w="fit-content"
                miw={110}
              />
            </Table.Td>
            <Table.Td w={100}>
              <Select
                defaultValue={prescription?.doseFrequencyID?.toString() || '0'}
                data={comboboxData.doseFrequencies}
                onChange={(value) => {
                  if (value && !value.startsWith('separator-'))
                    updatePrescriptionRow({ doseFrequencyID: Number(value) });
                }}
                error={
                  errors.find(
                    (error) =>
                      error.id == prescription?.id &&
                      error.doseFrequencyIDError,
                  )?.doseFrequencyIDError
                }
                w="fit-content"
                miw={130}
                maxDropdownHeight={500}
                styles={{
                  dropdown: {
                    minWidth: 'max-content',
                    whiteSpace: 'nowrap',
                  },
                  option: {
                    '&[data-disabled]': {
                      color: '#adb5bd',
                      cursor: 'default',
                      pointerEvents: 'none',
                      textAlign: 'center',
                      fontWeight: 'normal',
                      fontSize: '12px',
                    },
                  },
                }}
              />
            </Table.Td>
            <Table.Td>
              <CalendarDateInput
                maw={120}
                value={prescription?.startDate}
                onChange={(value) => {
                  updatePrescriptionRow({ startDate: value });
                }}
                error={
                  errors.find(
                    (error) =>
                      error.id == prescription?.id && error.startDateError,
                  )?.startDateError
                }
                styles={{
                  error: {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  },
                }}
              />
            </Table.Td>
            <Table.Td>
              <CalendarDateInput
                maw={120}
                value={prescription?.endDate}
                clearable
                placeholder={t('prescriptionHistory.ongoing')}
                onChange={(value) => {
                  updatePrescriptionRow({ endDate: value ?? undefined });
                }}
                error={
                  errors.find(
                    (error) =>
                      error.id == prescription?.id && error.endDateError,
                  )?.endDateError
                }
                styles={{
                  error: {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  },
                }}
              />
            </Table.Td>
            <Table.Td>
              <Badge
                color="gray"
                variant="light"
                leftSection={<IconLockOpen size={12} />}
              >
                {t('prescriptionHistory.badge.draft')}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Group gap="xs" wrap="nowrap">
                {isHCP && prescription.id > 0 && (
                  <Button
                    size="xs"
                    variant="outline"
                    color="green"
                    leftSection={<IconLock size={12} />}
                    onClick={() => onValidate(prescription.id)}
                  >
                    {t('prescriptionHistory.actions.validate')}
                  </Button>
                )}
                <ActionIcon
                  variant="subtle"
                  color={'gray'}
                  onClick={onClickDelete}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Group>
            </Table.Td>
          </Table.Tr>
        );
      },
    ),
  );

export default EditPrescriptionTableRow;
